import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api, { errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Mail, Sparkles, Lock, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const { setSessionFromTokens } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);

  const signIn = async () => {
    const em = email.trim().toLowerCase();
    if (!em || !em.includes("@")) return toast.error("Enter a valid email");
    if (!password) return toast.error("Enter your password");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email: em, password });
      setSessionFromTokens(data);
      toast.success(`Welcome, ${data.user.full_name}`);
      navigate("/dashboard");
    } catch (e) {
      toast.error(
        errorMessage(e, "Could not sign in"),
      );
      setPassword("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative">
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-primary/5 via-transparent to-brand-violet/5" />
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-2 mb-3">
            <Sparkles size={20} className="text-primary" />
            <span className="font-heading font-bold text-2xl tracking-tight">
              CA OS
            </span>
          </div>
          <div className="text-sm text-muted-foreground">
            The Operating System for Chartered Accountants
          </div>
        </div>

        <div
          className="border border-border rounded-xl bg-card p-6 shadow-sm space-y-5"
          data-testid="login-card"
        >
          <div>
            <h1 className="font-heading text-xl font-semibold">Sign in</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Sign-in is invitation-only. Enter your work email and password.
            </p>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              signIn();
            }}
            className="space-y-4"
          >
            <div className="space-y-1.5">
              <Label className="text-xs">Email</Label>
              <div className="relative">
                <Mail
                  size={13}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  type="email"
                  autoFocus
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@firm.com"
                  className="pl-9"
                  data-testid="login-email"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Password</Label>
              <div className="relative">
                <Lock
                  size={13}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your password"
                  className="pl-9 pr-9"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  data-testid="login-toggle-password"
                >
                  {showPassword ? <EyeOff size={13} /> : <Eye size={13} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={busy || !email || !password}
              data-testid="login-submit"
            >
              {busy && <Loader2 size={14} className="animate-spin mr-1.5" />}
              Sign in
            </Button>
          </form>

          <div className="text-center text-xs">
            <Link
              to="/forgot-password"
              className="text-primary hover:underline"
              data-testid="login-forgot"
            >
              Forgot your password?
            </Link>
          </div>
        </div>

        <div className="text-center mt-6 text-xs text-muted-foreground">
          By signing in you agree to our terms of service.
        </div>
      </div>
    </div>
  );
}
